/**
 * Velora Capture Helper detection (one helper per workstation).
 *
 * The helper (OpenImaging `VeloraCaptureHelper.exe`, per-user install from
 * `/downloads/capture-helper/`) turns the intraoral camera's handpiece
 * button (a UVC still trigger, which no browser API can see) into a key
 * press, and reports whether the Pulse Imaging capture agent (X-ray
 * sensors, TWAIN) is on the PC. It answers on the loopback address:
 *
 *   GET http://127.0.0.1:17470/velora-helper/v1/status   (17471 when 17470 was taken)
 *
 * with CORS locked to Velora's origin and Chrome's Private Network Access
 * preflight answered. `useCaptureHelper()` probes it with a short timeout,
 * again when the window regains focus and every 30 s, and reports one of:
 * `checking`, `running` (with version), `missing` (with the reason: blocked
 * by the browser's Local Network Access permission, refused, or not running;
 * localAccess.ts) or `outdated`.
 *
 * Velora also tells a running helper which key its capture surface expects
 * (`CAMERA_BUTTON_KEY`, POST /velora-helper/v1/key): the helper's default is
 * Space so it works with older Velora builds.
 */
import { useSyncExternalStore } from 'react';
import {
  browserPermissions,
  diagnoseMissing,
  queryLocalAccess,
  type HelperMissingReason,
  type LocalAccessState,
  type PermissionsLike,
} from './localAccess.js';
import { CAMERA_BUTTON_KEY } from './trigger.js';

/** The helper build served by this Velora (apps/web/public/downloads/capture-helper/). */
export const HELPER_RELEASE = {
  name: 'Velora Capture Helper',
  version: '1.1.1',
  /** One double-click installer (per user, no admin): OpenImaging Setup.cs. */
  file: 'VeloraCaptureHelperSetup-1.1.1.exe',
  sha256: 'c4271e9a7f63d0873be7213d06e31de60194d65f64d91f96cc746229a542d388',
  size: 35_174_801,
} as const;

export const HELPER_DOWNLOAD_URL = `/downloads/capture-helper/${HELPER_RELEASE.file}`;
/** Older helpers than this are reported `outdated` (an update is offered). */
export const HELPER_MIN_VERSION = HELPER_RELEASE.version;
export const HELPER_PORTS = [17470, 17471] as const;
export const HELPER_STATUS_PATH = '/velora-helper/v1/status';
export const HELPER_KEY_PATH = '/velora-helper/v1/key';
export const HELPER_PROBE_TIMEOUT_MS = 1500;
export const HELPER_REPROBE_MS = 30_000;

export type HelperCapability = 'cameraButton' | 'sensors' | 'twain' | 'documentScan';

export interface HelperInfo {
  name: string;
  version: string;
  port: number;
  capabilities: readonly string[];
  cameraButton: {
    device: string | null;
    listening: boolean;
    key: string | null;
    keys: readonly string[];
  } | null;
  /** The camera bridge (helper 1.1+): the helper owns the camera and serves it. */
  camera: HelperCamera | null;
  /** The document-scan service (helper 1.2+): TWAIN through the helper's 32-bit broker. */
  scanner?: { available: boolean; error: string | null } | null;
}

/** One camera the helper can serve. */
export interface HelperCameraEntry {
  device: string;
  path: string;
  /** The driver reports a capture (still-trigger) button. */
  button: boolean;
}

export interface HelperCamera {
  /** A camera is selected and can stream. */
  available: boolean;
  device: string | null;
  path: string | null;
  button: boolean;
  /** Route paths (absolute on the helper origin). */
  stream: string;
  events: string;
  still: string;
  cameras: readonly HelperCameraEntry[];
}

export type HelperState =
  | { state: 'checking' }
  | {
      state: 'missing';
      /** Why (set by the monitor; absent from a bare probe): see localAccess.ts. */
      reason?: HelperMissingReason;
      /** The browser's Local Network Access permission when it was checked. */
      permission?: LocalAccessState;
    }
  | ({ state: 'running' } & HelperInfo)
  | ({ state: 'outdated'; latest: string } & HelperInfo);

/** -1, 0 or 1 for dotted numeric versions ("1.0.10" > "1.0.9"); junk sorts lowest. */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) =>
    (v.trim().replace(/^v/i, '').split(/[+-]/)[0] ?? '')
      .split('.')
      .map((x) => (/^\d+$/.test(x) ? Number(x) : -1));
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/** Reads a status body; null when it is not the helper's. */
export function parseHelperStatus(body: unknown, port: number): HelperInfo | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (typeof b['version'] !== 'string' || typeof b['name'] !== 'string') return null;
  const caps = Array.isArray(b['capabilities'])
    ? (b['capabilities'] as unknown[]).filter((c): c is string => typeof c === 'string')
    : [];
  const cb = b['cameraButton'] as Record<string, unknown> | null | undefined;
  return {
    name: b['name'],
    version: b['version'],
    port,
    capabilities: caps,
    cameraButton:
      cb && typeof cb === 'object'
        ? {
            device: typeof cb['device'] === 'string' ? cb['device'] : null,
            listening: cb['listening'] === true,
            key: typeof cb['key'] === 'string' ? cb['key'] : null,
            keys: Array.isArray(cb['keys'])
              ? (cb['keys'] as unknown[]).filter((k): k is string => typeof k === 'string')
              : [],
          }
        : null,
    camera: parseCamera(b['camera']),
    scanner: parseScanner(b['scanner']),
  };
}

function parseScanner(raw: unknown): { available: boolean; error: string | null } | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  return {
    available: o['available'] === true,
    error: typeof o['error'] === 'string' ? o['error'] : null,
  };
}

const CAMERA_PREFIX = '/velora-helper/v1/camera/';

function parseCamera(raw: unknown): HelperCamera | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Record<string, unknown>;
  const str = (k: string) => (typeof c[k] === 'string' ? (c[k] as string) : null);
  // Routes must stay on the helper's camera prefix (never another path or host).
  const route = (k: string, d: string) => {
    const v = str(k);
    return v && v.startsWith(CAMERA_PREFIX) && !v.includes('..') ? v : CAMERA_PREFIX + d;
  };
  const cameras = Array.isArray(c['cameras'])
    ? (c['cameras'] as unknown[]).flatMap((x) => {
        const e = x as Record<string, unknown> | null;
        return e && typeof e['device'] === 'string' && typeof e['path'] === 'string'
          ? [{ device: e['device'], path: e['path'], button: e['button'] === true }]
          : [];
      })
    : [];
  return {
    available: c['available'] === true,
    device: str('device'),
    path: str('path'),
    button: c['button'] === true,
    stream: route('stream', 'stream'),
    events: route('events', 'events'),
    still: route('still', 'still'),
    cameras,
  };
}

/**
 * The helper's camera bridge when Velora should use it instead of
 * getUserMedia: the helper runs (any version that has the bridge), reports
 * the camera capability and has a camera with a capture button. The button
 * only reaches the process that owns the camera, so with a button camera the
 * helper must own it.
 */
export function helperBridge(s: HelperState): (HelperInfo & { camera: HelperCamera }) | null {
  if (s.state !== 'running' && s.state !== 'outdated') return null;
  const cam = s.camera;
  if (!cam || !s.capabilities.includes('camera') || !cam.available) return null;
  if (!cam.button && !cam.cameras.some((c) => c.button)) return null;
  return { ...s, camera: cam };
}

export function classify(info: HelperInfo | null, min = HELPER_MIN_VERSION): HelperState {
  if (!info) return { state: 'missing' };
  return compareVersions(info.version, min) < 0
    ? { state: 'outdated', latest: HELPER_RELEASE.version, ...info }
    : { state: 'running', ...info };
}

export type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

/** Loopback fetch options: no cookies, no cache; Chrome's Local Network Access hint. */
export const loopbackInit = (signal: AbortSignal, method = 'GET'): RequestInit =>
  ({
    method,
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    signal,
    targetAddressSpace: 'loopback',
  }) as RequestInit;

/** Probes every helper port at once; the first that answers as the helper wins. */
export async function probeCaptureHelper(
  fetchImpl: FetchLike = (u, i) => fetch(u, i),
  timeoutMs = HELPER_PROBE_TIMEOUT_MS,
): Promise<HelperState> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const one = async (port: number): Promise<HelperInfo> => {
      const res = await fetchImpl(
        `http://127.0.0.1:${port}${HELPER_STATUS_PATH}`,
        loopbackInit(ctrl.signal),
      );
      if (!res.ok) throw new Error(`status ${res.status}`);
      const info = parseHelperStatus(await res.json(), port);
      if (!info) throw new Error('not the helper');
      return info;
    };
    const info = await Promise.any(HELPER_PORTS.map(one)).catch(() => null);
    return classify(info);
  } finally {
    clearTimeout(timer);
    ctrl.abort();
  }
}

/** Asks a running helper to send `key` (best effort; older helpers have no such route). */
export async function setHelperKey(
  info: HelperInfo,
  key: string,
  fetchImpl: FetchLike = (u, i) => fetch(u, i),
): Promise<boolean> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), HELPER_PROBE_TIMEOUT_MS);
  try {
    const res = await fetchImpl(
      `http://127.0.0.1:${info.port}${HELPER_KEY_PATH}?key=${encodeURIComponent(key)}`,
      loopbackInit(ctrl.signal, 'POST'),
    );
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** True when the helper should be told our key (it sends another one and knows ours). */
export function wantsKey(info: HelperInfo, key = CAMERA_BUTTON_KEY): boolean {
  const cb = info.cameraButton;
  return !!cb && cb.key !== key && cb.keys.includes(key);
}

/* ------------------------------------------------------------------ */
/* Shared store: one probe loop for every subscriber                   */
/* ------------------------------------------------------------------ */

export interface HelperMonitorEnv {
  fetch: FetchLike;
  setInterval: (fn: () => void, ms: number) => unknown;
  clearInterval: (id: unknown) => void;
  /** Subscribes to window focus / tab visible; returns the removal. */
  onFocus: (fn: () => void) => () => void;
  /**
   * The browser's permissions API, to tell "blocked by the browser" from
   * "not running" (localAccess.ts). Absent: every miss reads `not_running`.
   */
  permissions?: PermissionsLike | null;
}

export function browserHelperEnv(): HelperMonitorEnv {
  return {
    fetch: (u, i) => fetch(u, i),
    setInterval: (fn, ms) => setInterval(fn, ms),
    clearInterval: (id) => clearInterval(id as ReturnType<typeof setInterval>),
    permissions: browserPermissions(),
    onFocus: (fn) => {
      const vis = () => document.visibilityState === 'visible' && fn();
      window.addEventListener('focus', fn);
      document.addEventListener('visibilitychange', vis);
      return () => {
        window.removeEventListener('focus', fn);
        document.removeEventListener('visibilitychange', vis);
      };
    },
  };
}

export interface HelperMonitor {
  get: () => HelperState;
  subscribe: (l: () => void) => () => void;
  /** Probes now (also what focus and the timer do). */
  refresh: () => Promise<HelperState>;
}

export function createHelperMonitor(env: HelperMonitorEnv, interval = HELPER_REPROBE_MS) {
  let value: HelperState = { state: 'checking' };
  const listeners = new Set<() => void>();
  let timer: unknown = null;
  let offFocus: (() => void) | null = null;
  let inflight: Promise<HelperState> | null = null;
  let keySentTo: string | null = null;
  let permWatch: (() => void) | null = null;

  // Re-probe as soon as the user changes the Local Network Access permission
  // (Allow in the prompt or in site settings), not only on focus or the timer.
  const watchPermission = () => {
    if (permWatch || !env.permissions || listeners.size === 0) return;
    void queryLocalAccess(env.permissions ?? null)
      .then((a) => {
        const st = a.status;
        if (!st?.addEventListener || permWatch || listeners.size === 0) return;
        const on = () => void refresh();
        st.addEventListener('change', on);
        permWatch = () => st.removeEventListener?.('change', on);
      })
      .catch(() => undefined);
  };

  const set = (next: HelperState) => {
    const same = JSON.stringify(next) === JSON.stringify(value);
    value = next;
    if (!same) for (const l of listeners) l();
  };

  const refresh = (): Promise<HelperState> => {
    if (inflight) return inflight;
    inflight = probeCaptureHelper(env.fetch)
      .then(async (first) => {
        // One missed probe of a running helper (busy, a slow machine) is
        // checked again before the camera panel falls back to the browser.
        let s =
          first.state === 'missing' && (value.state === 'running' || value.state === 'outdated')
            ? await probeCaptureHelper(env.fetch)
            : first;
        if (s.state === 'missing') {
          // Blocked by the browser, refused, or not running: each has its own fix.
          const d = await diagnoseMissing(env.fetch, env.permissions ?? null).catch(() => null);
          s = d ? { state: 'missing', reason: d.reason, permission: d.permission } : s;
          watchPermission();
        }
        if ((s.state === 'running' || s.state === 'outdated') && wantsKey(s)) {
          // Once per helper process (port + version): the helper keeps it in key.txt.
          const id = `${s.port}/${s.version}`;
          if (keySentTo !== id) {
            keySentTo = id;
            await setHelperKey(s, CAMERA_BUTTON_KEY, env.fetch);
          }
        }
        set(s);
        return s;
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  };

  const start = () => {
    void refresh();
    timer = env.setInterval(() => void refresh(), interval);
    offFocus = env.onFocus(() => void refresh());
  };
  const stop = () => {
    if (timer !== null) env.clearInterval(timer);
    timer = null;
    offFocus?.();
    offFocus = null;
    permWatch?.();
    permWatch = null;
  };

  return {
    get: () => value,
    subscribe: (l: () => void) => {
      listeners.add(l);
      if (listeners.size === 1) start();
      return () => {
        listeners.delete(l);
        if (listeners.size === 0) stop();
      };
    },
    refresh,
  } satisfies HelperMonitor;
}

let shared: HelperMonitor | null = null;
function sharedMonitor(): HelperMonitor {
  shared ??= createHelperMonitor(browserHelperEnv());
  return shared;
}

/** Test seam: replace (or reset with null) the app-wide monitor. */
export function setHelperMonitorForTests(m: HelperMonitor | null): void {
  shared = m;
}

/** The capture helper's state on this workstation; probes while mounted. */
export function useCaptureHelper(): HelperState & { refresh: () => Promise<HelperState> } {
  const m = sharedMonitor();
  const s = useSyncExternalStore(m.subscribe, m.get, m.get);
  return { ...s, refresh: m.refresh };
}

export const helperRunning = (s: HelperState): s is Extract<HelperState, { state: 'running' }> =>
  s.state === 'running';

/* ------------------------------------------------------------------ */
/* "Continue without it" (per browser session)                         */
/* ------------------------------------------------------------------ */

const DISMISS_KEY = 'velora.capture.helper.dismissed';
const dismissListeners = new Set<() => void>();

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function setHelperPromptDismissed(v: boolean): void {
  try {
    if (v) sessionStorage.setItem(DISMISS_KEY, '1');
    else sessionStorage.removeItem(DISMISS_KEY);
  } catch {
    /* private mode: dismissal lasts until reload */
  }
  memDismissed = v;
  for (const l of dismissListeners) l();
}

let memDismissed: boolean | null = null;
const getDismissed = () => memDismissed ?? readDismissed();

export function useHelperPromptDismissed(): boolean {
  return useSyncExternalStore(
    (l) => {
      dismissListeners.add(l);
      return () => {
        dismissListeners.delete(l);
      };
    },
    getDismissed,
    getDismissed,
  );
}

/** "29.8 MB" */
export function formatSize(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/* ------------------------------------------------------------------ */
/* "Use browser camera" (per browser session)                         */
/* ------------------------------------------------------------------ */

const BROWSER_CAMERA_KEY = 'velora.capture.helper.browserCamera';
const browserListeners = new Set<() => void>();
let memBrowser: boolean | null = null;
const getBrowserChosen = () => {
  if (memBrowser !== null) return memBrowser;
  try {
    return sessionStorage.getItem(BROWSER_CAMERA_KEY) === '1';
  } catch {
    return false;
  }
};

export function setBrowserCameraChosen(v: boolean): void {
  try {
    if (v) sessionStorage.setItem(BROWSER_CAMERA_KEY, '1');
    else sessionStorage.removeItem(BROWSER_CAMERA_KEY);
  } catch {
    /* private mode */
  }
  memBrowser = v;
  for (const l of browserListeners) l();
}

/** Whether the user chose the browser camera over the helper's for this session. */
export function useBrowserCameraChosen(): [boolean, (v: boolean) => void] {
  const v = useSyncExternalStore(
    (l) => {
      browserListeners.add(l);
      return () => {
        browserListeners.delete(l);
      };
    },
    getBrowserChosen,
    getBrowserChosen,
  );
  return [v, setBrowserCameraChosen];
}
